import {
  ActionIcon,
  Box,
  CloseButton,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { IconPlus, IconSearch, IconX } from '@tabler/icons-react';
import { CSSProperties, KeyboardEvent, ReactNode, useId, useRef, useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useClients } from 'src/clients/hooks/useClients';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { FIELD_STYLES } from 'src/design-system/InputWithLabel';
import { NumberInputWithLabel } from 'src/design-system/NumberInputWithLabel';
import { PersonResultRow } from 'src/design-system/PersonResultRow';
import { ownerCandidates } from 'src/financial/financial-data/financial-item/ownerCandidates';
import { roundShare } from 'src/financial/financial-data/ownerShares';
import { matchesClientSearch } from 'src/utils/clientSearch';
import { shareInPounds } from '../../joint/owners';
import { moneyOrNoFigure } from '../../notRecorded';
import { Allocation, AllocationConfig, NO_PEOPLE, SHARE_KEY, toAmount } from './allocations';
import { MoneyInput } from './MoneyInput';

const SHARE_COLUMN_WIDTH = 96;
const SHARE_AMOUNT_COLUMN_WIDTH = 112;
const MONEY_COLUMN_WIDTH = 140;
const REMOVE_COLUMN_WIDTH = 28;
// The height of an input, so a cell of text lines up with the inputs beside it
const CELL_HEIGHT = 36;
const MAX_RESULTS = 8;
const ROW_BORDER = '1px solid var(--mantine-color-gray-2)';

type Person = { clientId: string; name: string; subtitle?: string };

/** A cell of text in a row of inputs, centred on the inputs' height rather than the row's. */
function TextCell({ children, role = 'cell' }: { children: ReactNode; role?: string }) {
  return (
    <Box role={role} miw={0} mih={CELL_HEIGHT} style={{ display: 'flex', alignItems: 'center' }}>
      {children}
    </Box>
  );
}

/**
 * "Who does this apply to?" as one table, a row per person with their figures in shared columns,
 * as the Financial tab lays out its owners: name, share, and what the share comes to, then any
 * figures of the person's own. Its last row is "Add person", which turns into a search in place:
 * it opens on the family, who most items are shared with, and typing finds any client the adviser
 * can open, since an item can be shared by people in different families.
 */
export function PeopleAllocations({
  members,
  allocations,
  share,
  extraFields,
  nameOf,
  viewingClientId,
  error,
  fieldError,
  onAdd,
  onRemove,
  onAmountChange,
}: AllocationConfig) {
  const currency = useCurrency();
  const idPrefix = useId();
  const fieldId = (clientId: string, key: string) => `${idPrefix}-${clientId}-${key}`;
  const addPersonRef = useRef<HTMLButtonElement>(null);
  const { clients } = useClients({});
  const [searching, setSearching] = useState(false);
  const [search, setSearch] = useState('');

  const familyClientIds = new Set(members.map(member => member.clientId));
  const allocatedClientIds = new Set(allocations.map(allocation => allocation.clientId));
  const familyNameOf = new Map(clients.map(client => [client.id, client.family?.name] as const));
  const personFor = (clientId: string): Person => ({
    clientId,
    name: nameOf(clientId),
    subtitle: familyClientIds.has(clientId) ? undefined : familyNameOf.get(clientId),
  });
  // Typed results mix the family with everyone else, so a family member says so there
  const searchSubtitle = (person: Person) =>
    familyClientIds.has(person.clientId) ? 'This family' : person.subtitle;

  const typed = search.trim();
  const familyMatches = members
    .filter(member => !allocatedClientIds.has(member.clientId))
    .filter(member => matchesClientSearch([member.firstName, member.lastName], typed))
    .map(member => personFor(member.clientId));
  const otherMatches = ownerCandidates(
    clients,
    new Set([...familyClientIds, ...allocatedClientIds]),
    typed,
  ).map(client => personFor(client.id));
  const suggestions =
    typed === '' ? familyMatches : [...familyMatches, ...otherMatches].slice(0, MAX_RESULTS);
  // Enter adds the top match once something is typed. Before that the list is just the family on
  // offer, and an Enter there would add whoever happens to be listed first.
  const enterPick = typed === '' ? undefined : suggestions[0];
  // On a client's tab the item starts with them, so the rest of the family is what is left
  const familyHeading = allocations.some(allocation => familyClientIds.has(allocation.clientId))
    ? 'Rest of the family'
    : 'Family';

  const closeSearch = () => {
    setSearching(false);
    setSearch('');
    // The search the focus was in is gone, so it goes back to the row that opened it
    requestAnimationFrame(() => addPersonRef.current?.focus());
  };

  // The search stays open after adding someone, since an item is often shared by several people;
  // the cursor moves to the new person's share, and the search is a click away for the next.
  const add = (clientId: string) => {
    onAdd(clientId);
    setSearch('');
    // The row the new person lands in only exists after the next render.
    requestAnimationFrame(() => document.getElementById(fieldId(clientId, SHARE_KEY))?.focus());
  };

  const handleSearchKeys = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      closeSearch();
      return;
    }
    if (event.key !== 'Enter' || !enterPick) return;
    event.preventDefault();
    add(enterPick.clientId);
  };

  // The share a row reads at: as typed, or the even split a blank share stands for.
  const shownShare = (allocation: Allocation) => {
    const typedShare = allocation.amounts[SHARE_KEY];
    if (typeof typedShare === 'number') return { percentage: typedShare, isAssumed: false };
    if (share.assumed !== null) return { percentage: share.assumed, isAssumed: true };
    return null;
  };
  const sharePlaceholder = share.assumed === null ? '0' : String(roundShare(share.assumed));

  const gridRow: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: [
      'minmax(0, 1fr)',
      `${SHARE_COLUMN_WIDTH}px`,
      `${SHARE_AMOUNT_COLUMN_WIDTH}px`,
      ...extraFields.map(() => `${MONEY_COLUMN_WIDTH}px`),
      `${REMOVE_COLUMN_WIDTH}px`,
    ].join(' '),
    columnGap: 20,
    // An error under one figure grows its own cell downwards rather than pushing its row's other
    // figures out of line
    alignItems: 'start',
  };
  const lastRowBorder = allocations.length > 0 ? ROW_BORDER : undefined;

  const heading = (key: string, label: string, align: 'left' | 'right' = 'left') => (
    <Text key={key} role="columnheader" size="xs" c="gray.7" ta={align}>
      {label}
    </Text>
  );

  const rowFor = (allocation: Allocation, index: number) => {
    const person = personFor(allocation.clientId);
    const isViewer = allocation.clientId === viewingClientId;
    const shareId = fieldId(allocation.clientId, SHARE_KEY);
    const shown = shownShare(allocation);
    const comesTo = shown && share.of !== null ? shareInPounds(share.of, shown.percentage) : null;
    return (
      <Box
        key={allocation.clientId}
        role="row"
        data-testid={`allocation-${allocation.clientId}`}
        style={{
          ...gridRow,
          padding: '10px 12px 10px 16px',
          borderTop: index > 0 ? ROW_BORDER : undefined,
        }}
      >
        <TextCell role="rowheader">
          <Box miw={0}>
            <Text size="sm" fw={FONT_WEIGHTS.semibold} c="gray.10" truncate>
              {isViewer ? `${person.name} (this client)` : person.name}
            </Text>
            {person.subtitle && (
              <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.7" truncate>
                {person.subtitle}
              </Text>
            )}
          </Box>
        </TextCell>
        <Box role="cell">
          <NumberInputWithLabel
            id={shareId}
            aria-label={`Share for ${person.name}`}
            aria-describedby={`${shareId}-amount`}
            rightSection="%"
            max={100}
            clampBehavior="none"
            placeholder={sharePlaceholder}
            value={allocation.amounts[SHARE_KEY] ?? ''}
            onChange={value => onAmountChange(allocation.clientId, SHARE_KEY, toAmount(value))}
            error={fieldError?.(allocation, SHARE_KEY)}
            min={0}
            decimalScale={2}
            hideControls
          />
        </Box>
        <TextCell>
          <Text
            id={`${shareId}-amount`}
            size="sm"
            w="100%"
            ta="right"
            c={shown?.isAssumed ? 'gray.6' : 'gray.10'}
            fw={FONT_WEIGHTS.medium}
            data-testid="share-amount"
          >
            {moneyOrNoFigure(comesTo, currency)}
          </Text>
        </TextCell>
        {extraFields.map(field => (
          <Box key={field.key} role="cell">
            <MoneyInput
              id={fieldId(allocation.clientId, field.key)}
              aria-label={`${field.label} for ${person.name}`}
              value={allocation.amounts[field.key] ?? ''}
              onChange={value => onAmountChange(allocation.clientId, field.key, value)}
              error={fieldError?.(allocation, field.key)}
            />
          </Box>
        ))}
        <TextCell>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => onRemove(allocation.clientId)}
            aria-label={`Remove ${person.name}`}
          >
            <IconX size={16} />
          </ActionIcon>
        </TextCell>
      </Box>
    );
  };

  return (
    <Stack gap="sm">
      <Text id={`${idPrefix}-heading`} c="gray.10" fw={FONT_WEIGHTS.semibold} fz={16} lh="24px">
        Who does this apply to?
      </Text>

      <Box style={{ border: ROW_BORDER, borderRadius: 16 }}>
        {allocations.length === 0 ? (
          <Text c="gray.7" fw={FONT_WEIGHTS.medium} fz={14} lh="20px" px={16} py={12}>
            {NO_PEOPLE}
          </Text>
        ) : (
          <Box role="table" aria-labelledby={`${idPrefix}-heading`}>
            <Box role="row" style={{ ...gridRow, alignItems: 'end', padding: '10px 12px 0 16px' }}>
              {heading('person', 'Person')}
              {heading(SHARE_KEY, 'Share')}
              {heading(`${SHARE_KEY}-amount`, share.amountLabel, 'right')}
              {extraFields.map(field => heading(field.key, field.label))}
              <Box role="columnheader" aria-label="Remove" />
            </Box>
            {allocations.map(rowFor)}
          </Box>
        )}

        {!searching && (
          <UnstyledButton
            ref={addPersonRef}
            onClick={() => setSearching(true)}
            data-testid="add-person"
            w="100%"
            px={16}
            py={12}
            c="brand.6"
            fw={FONT_WEIGHTS.semibold}
            fz={14}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              borderTop: lastRowBorder,
              borderRadius: allocations.length > 0 ? '0 0 16px 16px' : 16,
            }}
          >
            <IconPlus size={16} stroke={2.5} />
            Add person
          </UnstyledButton>
        )}

        {searching && (
          <Box p="xs" style={{ borderTop: lastRowBorder }}>
            <TextInput
              autoFocus
              value={search}
              onChange={event => setSearch(event.currentTarget.value)}
              onKeyDown={handleSearchKeys}
              // Mantine's modal closes on Escape from anywhere inside it unless the key came from
              // an element marked like this; here Escape should leave the search, not the form.
              data-mantine-stop-propagation
              placeholder="Search clients"
              aria-label="Search clients"
              leftSection={<IconSearch size={16} />}
              rightSection={
                <CloseButton size="sm" onClick={closeSearch} aria-label="Stop adding people" />
              }
              // An input's right section lets clicks through to the input by default, which would
              // leave the close button above unclickable
              rightSectionPointerEvents="all"
              styles={FIELD_STYLES}
            />
            {(suggestions.length > 0 || typed !== '') && (
              <Stack gap={2} mt={6}>
                {typed === '' && (
                  <Text size="xs" fw={FONT_WEIGHTS.semibold} c="gray.7" px="xs" pt={4}>
                    {familyHeading}
                  </Text>
                )}
                {suggestions.map(person => (
                  <PersonResultRow
                    key={person.clientId}
                    title={person.name}
                    subtitle={typed === '' ? undefined : searchSubtitle(person)}
                    actionLabel="Add"
                    actionAriaLabel={`Add ${person.name}`}
                    onAction={() => add(person.clientId)}
                    stretchAction
                    isActive={person === enterPick}
                  />
                ))}
                {typed !== '' && suggestions.length === 0 && (
                  <Text size="sm" c="gray.6" px="xs" py={6}>
                    No matching clients
                  </Text>
                )}
              </Stack>
            )}
          </Box>
        )}
      </Box>

      {error && (
        <Text
          c="red.7"
          fw={FONT_WEIGHTS.medium}
          fz={14}
          lh="20px"
          role="alert"
          data-testid="allocation-error"
        >
          {error}
        </Text>
      )}
    </Stack>
  );
}
